import React, { useState, useEffect } from 'react';
import {
    FaPlus,
    FaTrash,
    FaEdit,
    FaSave,
    FaTimes,
    FaSpinner,
    FaDollarSign,
    FaFileInvoice
} from 'react-icons/fa';
import { API_ENDPOINTS } from '../../config/api';
import { useAdminAuth } from '../context/AdminAuthContext';

const SERVICE_TYPES = [
    { value: 'website', label: 'Website Design & Development' },
    { value: 'email', label: 'Email Services' },
    { value: 'branding', label: 'Branding & Logo' },
    { value: 'cloud', label: 'Cloud Services' },
    { value: 'networking', label: 'Networking Solutions' },
    { value: 'support', label: 'Ongoing Premium Support' },
    { value: 'other', label: 'Other' }
];

const ITServicesInvoiceForm = ({ onSave, onCancel }) => {
    const { token } = useAdminAuth();
    const [formData, setFormData] = useState({
        customerEmail: '',
        customerName: '',
        customerPhone: '',
        customerCompany: '',
        customerAddress: '',
        serviceType: 'website',
        lineItems: [
            { description: '', quantity: 1, unitPrice: 0, total: 0 }
        ],
        paymentType: 'one-time',
        recurringSchedule: 'monthly',
        dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        notes: ''
    });

    const [calculations, setCalculations] = useState({
        subtotal: 0,
        tax: 0,
        total: 0
    });

    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        calculateTotals();
    }, [formData.lineItems]);

    const calculateTotals = () => {
        const totalInclusive = formData.lineItems.reduce((sum, item) => {
            const total = (item.quantity || 1) * (item.unitPrice || 0);
            return sum + total;
        }, 0);

        const tax = totalInclusive / 11;
        const subtotal = totalInclusive - tax;
        const total = totalInclusive;

        setCalculations({
            subtotal,
            tax,
            total
        });
    };

    const handleLineItemChange = (index, field, value) => {
        const updatedItems = [...formData.lineItems];
        updatedItems[index] = {
            ...updatedItems[index],
            [field]: field === 'quantity' || field === 'unitPrice' ? parseFloat(value) || 0 : value
        };

        const quantity = updatedItems[index].quantity || 1;
        const unitPrice = updatedItems[index].unitPrice || 0;
        updatedItems[index].total = quantity * unitPrice;

        setFormData({ ...formData, lineItems: updatedItems });
    };

    const addLineItem = () => {
        setFormData({
            ...formData,
            lineItems: [
                ...formData.lineItems,
                { description: '', quantity: 1, unitPrice: 0, total: 0 }
            ]
        });
    };

    const removeLineItem = (index) => {
        if (formData.lineItems.length > 1) {
            const updatedItems = formData.lineItems.filter((_, i) => i !== index);
            setFormData({ ...formData, lineItems: updatedItems });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);
        setError(null);

        // Validation
        if (!formData.customerEmail || !formData.customerName) {
            setError('Customer email and name are required');
            setSaving(false);
            return;
        }

        try {
            const invoiceData = {
                customerEmail: formData.customerEmail,
                customerName: formData.customerName,
                customerPhone: formData.customerPhone || null,
                customerCompany: formData.customerCompany || null,
                customerAddress: formData.customerAddress || null,
                serviceType: SERVICE_TYPES.find(s => s.value === formData.serviceType)?.label,
                lineItems: formData.lineItems,
                subtotal: calculations.subtotal,
                tax: calculations.tax,
                totalAmount: calculations.total,
                paymentType: formData.paymentType,
                paymentSchedule: formData.paymentType === 'recurring' ? formData.recurringSchedule : null,
                dueDate: new Date(formData.dueDate).toISOString(),
                metadata: {
                    notes: formData.notes,
                    isRecurring: formData.paymentType === 'recurring',
                    recurringSchedule: formData.paymentType === 'recurring' ? formData.recurringSchedule : null
                }
            };

            const response = await fetch(API_ENDPOINTS.IT_SERVICES_INVOICE, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(token ? { 'Authorization': token } : {}) // HTTP API: No Bearer prefix
                },
                body: JSON.stringify({
                    action: 'createStandaloneInvoice',
                    invoiceData
                })
            });

            const data = await response.json();

            if (data.success) {
                if (onSave) {
                    onSave(data.invoice);
                }
            } else {
                setError(data.error || 'Failed to create invoice');
            }
        } catch (err) {
            console.error('Error creating invoice:', err);
            setError('Failed to create invoice. Please try again.');
        } finally {
            setSaving(false);
        }
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-AU', {
            style: 'currency',
            currency: 'AUD'
        }).format(amount);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
                <div className="bg-red-500/20 border border-red-500/50 rounded-lg p-4">
                    <p className="text-red-300">{error}</p>
                </div>
            )}

            {/* Customer Information */}
            <div className="bg-[#282828] rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Customer Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Customer Name *</label>
                        <input
                            type="text"
                            required
                            value={formData.customerName}
                            onChange={(e) => setFormData({ ...formData, customerName: e.target.value })}
                            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                            placeholder="John Doe"
                        />
                    </div>
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Customer Email *</label>
                        <input
                            type="email"
                            required
                            value={formData.customerEmail}
                            onChange={(e) => setFormData({ ...formData, customerEmail: e.target.value })}
                            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                            placeholder="customer@example.com"
                        />
                    </div>
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Company</label>
                        <input
                            type="text"
                            value={formData.customerCompany}
                            onChange={(e) => setFormData({ ...formData, customerCompany: e.target.value })}
                            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                            placeholder="Company Name"
                        />
                    </div>
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Address</label>
                        <input
                            type="text"
                            value={formData.customerAddress}
                            onChange={(e) => setFormData({ ...formData, customerAddress: e.target.value })}
                            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                            placeholder="123 Business St, Sydney NSW 2000"
                        />
                    </div>
                </div>
            </div>

            {/* Line Items */}
            <div className="bg-[#282828] rounded-lg p-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-xl font-bold text-[#ebdbb2]">Line Items</h3>
                    <button
                        type="button"
                        onClick={addLineItem}
                        className="bg-green-500/20 text-green-300 px-4 py-2 rounded-lg hover:bg-green-500/30 transition-colors flex items-center space-x-2"
                    >
                        <FaPlus />
                        <span>Add Item</span>
                    </button>
                </div>

                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-[#504945]">
                                <th className="text-left py-3 px-4 text-[#a89984] text-sm font-semibold">Description</th>
                                <th className="text-right py-3 px-4 text-[#a89984] text-sm font-semibold">Quantity</th>
                                <th className="text-right py-3 px-4 text-[#a89984] text-sm font-semibold">Unit Price</th>
                                <th className="text-right py-3 px-4 text-[#a89984] text-sm font-semibold">Total</th>
                                <th className="text-center py-3 px-4 text-[#a89984] text-sm font-semibold">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {formData.lineItems.map((item, index) => (
                                <tr key={index} className="border-b border-[#504945]">
                                    <td className="py-3 px-4">
                                        <input
                                            type="text"
                                            required
                                            value={item.description}
                                            onChange={(e) => handleLineItemChange(index, 'description', e.target.value)}
                                            className="w-full px-3 py-2 bg-[#3c3836] border border-[#504945] rounded text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                                            placeholder="Service description"
                                        />
                                    </td>
                                    <td className="py-3 px-4">
                                        <input
                                            type="number"
                                            min="1"
                                            value={item.quantity}
                                            onChange={(e) => handleLineItemChange(index, 'quantity', e.target.value)}
                                            className="w-full px-3 py-2 bg-[#3c3836] border border-[#504945] rounded text-[#ebdbb2] text-right focus:outline-none focus:border-[#FFD700]"
                                        />
                                    </td>
                                    <td className="py-3 px-4">
                                        <input
                                            type="number"
                                            min="0"
                                            step="0.01"
                                            value={item.unitPrice}
                                            onChange={(e) => handleLineItemChange(index, 'unitPrice', e.target.value)}
                                            className="w-full px-3 py-2 bg-[#3c3836] border border-[#504945] rounded text-[#ebdbb2] text-right focus:outline-none focus:border-[#FFD700]"
                                        />
                                    </td>
                                    <td className="py-3 px-4 text-right text-[#ebdbb2] font-semibold">
                                        {formatCurrency(item.total)}
                                    </td>
                                    <td className="py-3 px-4 text-center">
                                        {formData.lineItems.length > 1 && (
                                            <button
                                                type="button"
                                                onClick={() => removeLineItem(index)}
                                                className="p-2 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                                            >
                                                <FaTrash />
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mt-4 flex justify-end">
                    <div className="w-64 space-y-2">
                        <div className="flex justify-between text-[#ebdbb2]">
                            <span>Subtotal:</span>
                            <span>{formatCurrency(calculations.subtotal)}</span>
                        </div>
                        <div className="flex justify-between text-[#ebdbb2]">
                            <span>GST (10%):</span>
                            <span>{formatCurrency(calculations.tax)}</span>
                        </div>
                        <div className="flex justify-between text-[#ebdbb2] font-bold text-xl pt-2 border-t border-[#504945]">
                            <span>Total:</span>
                            <span className="text-[#FFD700]">{formatCurrency(calculations.total)}</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payment Information */}
            <div className="bg-[#282828] rounded-lg p-6">
                <h3 className="text-xl font-bold text-[#ebdbb2] mb-4">Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Due Date</label>
                        <input
                            type="date"
                            value={formData.dueDate}
                            onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                        />
                    </div>
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Service Category</label>
                        <select
                            value={formData.serviceType}
                            onChange={(e) => setFormData({ ...formData, serviceType: e.target.value })}
                            className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                        >
                            {SERVICE_TYPES.map(s => (
                                <option key={s.value} value={s.value}>{s.label}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div>
                        <label className="block text-[#a89984] text-sm mb-2">Payment Frequency</label>
                        <div className="flex space-x-4">
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, paymentType: 'one-time' })}
                                className={`flex-1 py-2 rounded-lg font-semibold transition-all ${formData.paymentType === 'one-time'
                                    ? 'bg-[#FFD700] text-[#1d2021]'
                                    : 'bg-[#3c3836] text-[#a89984] border border-[#504945] hover:bg-[#504945]'
                                    }`}
                            >
                                One-time
                            </button>
                            <button
                                type="button"
                                onClick={() => setFormData({ ...formData, paymentType: 'recurring' })}
                                className={`flex-1 py-2 rounded-lg font-semibold transition-all ${formData.paymentType === 'recurring'
                                    ? 'bg-[#FFD700] text-[#1d2021]'
                                    : 'bg-[#3c3836] text-[#a89984] border border-[#504945] hover:bg-[#504945]'
                                    }`}
                            >
                                Recurring
                            </button>
                        </div>
                    </div>
                    {formData.paymentType === 'recurring' && (
                        <div>
                            <label className="block text-[#a89984] text-sm mb-2">Billing Cycle</label>
                            <select
                                value={formData.recurringSchedule}
                                onChange={(e) => setFormData({ ...formData, recurringSchedule: e.target.value })}
                                className="w-full px-4 py-2 bg-[#3c3836] border border-[#504945] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#FFD700]"
                            >
                                <option value="monthly">Monthly</option>
                                <option value="quarterly">Quarterly</option>
                                <option value="yearly">Yearly</option>
                            </select>
                        </div>
                    )}
                </div>
            </div>

            <div className="flex justify-end space-x-4">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-6 py-3 bg-[#504945] text-[#ebdbb2] rounded-lg font-semibold hover:bg-[#665c54] transition-colors"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={saving}
                    className="px-6 py-3 bg-gradient-to-r from-[#FFD700] to-[#FFB700] text-[#1d2021] rounded-lg font-semibold hover:shadow-lg transition-all flex items-center space-x-2 disabled:opacity-50"
                >
                    {saving ? <FaSpinner className="animate-spin" /> : <FaSave />}
                    <span>Create Payable Invoice</span>
                </button>
            </div>
        </form>
    );
};

export default ITServicesInvoiceForm;
